/* eslint-disable no-dupe-class-members */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/**
 * @file
 */
import React from 'react';
import { discovery } from './Discovery';

function isClass(fcnOrCls) {
  return Object.hasOwn(fcnOrCls, 'prototype') && !Object.hasOwn(fcnOrCls, 'arguments');
}

/**
 * @type
 */
export type Exact<T, U> =
  U extends T
    ? Exclude<keyof U, keyof T> extends never // No extra properties/methods allowed
      ? Exclude<keyof T, keyof U> extends keyof T // Allow missing optional properties/methods
        ? U
        : never
      : never
  : never;

/**
 * @class
 */
export class WebPlatform {
    _implementations: Record<string, any>;
    _initialized: boolean;

    initialize() {
      if (!this._implementations) {
        this._implementations = {};
      }
      const dropinNames = Object.keys(discovery.dropins);
      if (dropinNames.length) {
        this._initialized = true;
      }
      dropinNames.forEach(c => {
        const interfaceId = discovery.dropins[c].implements;
        if (!interfaceId) {
          return;
        }
        if (!this._implementations[interfaceId]) {
          this._implementations[interfaceId] = {
            implementations: {},
          };
        }
        if (discovery.dropins[c].default && !this._implementations[interfaceId].defaultImplementation) {
          this._implementations[interfaceId].defaultImplementation = c;
        } else {
          this._implementations[interfaceId].implementations[c] = {};
        }
      });
      const providerNames = Object.keys(discovery.providers);
      if (providerNames.length) {
        this._initialized = true;
      }
      providerNames.forEach(c => {
        const interfaceId = discovery.providers[c].implements;
        if (!interfaceId) {
          return;
        }
        if (!this._implementations[interfaceId]) {
          this._implementations[interfaceId] = {
            implementations: {},
          };
        }
        if (discovery.providers[c].default && !this._implementations[interfaceId].defaultImplementation) {
          this._implementations[interfaceId].defaultImplementation = c;
        } else {
          this._implementations[interfaceId].implementations[c] = {};
        }
      });
    }

    /**
     * @description
     * Registers a default implementation for a provided interfaceId
     * @method
     * @param {string} interfaceId -- interfaceId associated with the component
     * @param {T} component -- id or class or function which can give the implementation of the component
     * @param {...config} config -- list of configs to initialize the component
     * @public
     */
    registerDefaultImplementationWith<T>(interfaceId: string, component: T | ((...config: any[]) => T) | { new (...config: any[]): T }, ...config: any[]): void {
      if (!interfaceId || !component) {
        throw new Error('Invalid interfaceId or component');
      }
      if (!this._implementations) {
        this.initialize();
      }
      if (!this._implementations[interfaceId]) {
        this._implementations[interfaceId] = {
          implementations: {},
        };
      }
      // We trust that component class provided implements the interface referred by interface id
      if (!this._implementations[interfaceId].defaultImplementation) {
        this._implementations[interfaceId].defaultImplementation = component;
        this._implementations[interfaceId].defaultImplementationConfig = config;
      }
    }

    /**
     * @description
     * Registers a default implementation for a provided interfaceId
     * @method
     * @param {string} interfaceId -- interfaceId associated with the component
     * @param {string} componentId -- id which can give the implementation of the component
     * @param {...config} config -- list of configs to initialize the component
     * @public
     */
    registerDefaultImplementation(interfaceId: string, componentId: string, ...config): void {
      if (!interfaceId || !componentId) {
        throw new Error('Invalid interfaceId or component');
      }
      if (!this._implementations) {
        this.initialize();
      }
      if (!this._implementations[interfaceId]) {
        this._implementations[interfaceId] = {
          implementations: {},
        };
      }
      // We trust that component class provided implements the interface referred by interface id
      if (!this._implementations[interfaceId].defaultImplementation) {
        this._implementations[interfaceId].defaultImplementation = componentId;
        this._implementations[interfaceId].defaultImplementationConfig = config;
      }
    }
    /**
     * @description
     * Registers an implementation for a provided interfaceId and/or componentId
     * @method
     * @param {string} interfaceId -- interfaceId associated with the component
     * @param {T} component -- class or function which can give the implementation of the component
     * @param {...config} config -- list of configs to initialize the component
     * @public
     */
    registerImplementationWith<T>(interfaceId: string, component: T | ((...config: any[]) => T) | { new (...config: any[]): T }, ...config: any[]): void {
      if (!interfaceId || !component) {
        throw new Error('Invalid interfaceId or component');
      }
      if (!this._initialized) {
        this.initialize();
      }
      if (!this._implementations[interfaceId]) {
        throw new Error('No such interface known');
      }
      // We trust that component class provided implements the interface referred by interface id
      if (!this._implementations[interfaceId].activeImplementation) {
        this._implementations[interfaceId].activeImplementation = component;
        this._implementations[interfaceId].activeImplementationConfig = config || [];
      }
    }
    /**
     * @description
     * Registers an implementation for a provided interfaceId and componentId
     * @method
     * @param {string} interfaceId -- interfaceId associated with the component
     * @param {string} componentId -- componentId to register the implementation
     * @param {T} component -- class or function which can give the implementation of the component
     * @param {...config} config -- list of configs to initialize the component
     * @public
     */
    registerImplementationAndIdWith<T>(interfaceId: string, componentId: string, component: T | ((...config: any[]) => T) | { new (...config: any[]): T }, ...config: any[]): void {
      if (!interfaceId || !componentId || !component) {
        throw new Error('Invalid interfaceId or componentId or component');
      }
      if (!this._initialized) {
        this.initialize();
      }
      if (!this._implementations[interfaceId]) {
        throw new Error('No such interface known');
      }
      if (!this._implementations[interfaceId].implementations[componentId]) {
        this._implementations[interfaceId].implementations[componentId] = {
          implementation: component,
          implementationConfig: config || [],
        };
      }
    }
    /**
     * @description
     * Registers an implementation for a provided interfaceId and/or componentId
     * @method
     * @param {string} interfaceId -- interfaceId associated with the component
     * @param {string} componentId -- id which can give the implementation of the component
     * @param {...config} config -- list of configs to initialize the component
     * @public
     */
    registerImplementation(interfaceId: string, componentId: string, ...config: any[]): void {
      if (!interfaceId || !componentId) {
        throw new Error('Invalid interfaceId or component');
      }
      if (!this._initialized) {
        this.initialize();
      }
      if (!this._implementations[interfaceId]) {
        throw new Error('No such interface known');
      }
      if (discovery.dropins[componentId]?.implements !== interfaceId && discovery.providers[componentId]?.implements !== interfaceId) {
        throw new Error('Provided component id does not implement the provided interfaceId');
      }
      if (!this._implementations[interfaceId].activeImplementation) {
        this._implementations[interfaceId].activeImplementation = componentId;
        this._implementations[interfaceId].activeImplementationConfig = config || [];
      }
    }

    locateImplementationSync(interfaceId: string): any;
    locateImplementationSync<T>(interfaceId: string): T;
    /**
     * @description
     * Locate the implementation for a provided interfaceId in a synchronous way
     * Can only be used with dc-core static providers
     * @method
     * @param {string} interfaceId -- interfaceId associated with the componente
     * @returns {object} - implementation object registered for the interface
     * @public
     */
    locateImplementationSync<T>(interfaceId: string): T {
      if (!interfaceId) {
        throw new Error('invalid arguments - supply a valid interface id');
      }
      if (!this._initialized) {
        this.initialize();
      }
      if (!this._implementations[interfaceId]) {
        throw new Error('No such interface known');
      }
      const impl = this._implementations[interfaceId].activeImplementation || this._implementations[interfaceId].defaultImplementation;
      if (!impl) {
        throw new Error('No implementation found for the given interface');
      }
      if (interfaceId.startsWith('React.')
        || typeof impl.getInstance === 'function'
        || typeof impl === 'function'
      ) {
        throw new Error('use locateImplementation instead. This is a sync version.');
      }
      if (!this._implementations[interfaceId].instance) {
        this._implementations[interfaceId].instance = impl;
      }
      return this._implementations[interfaceId].instance;
    }

    locateImplementation(interfaceId: string, componentId?: string, ...componentConfig): Promise<any>;
    locateImplementation<T>(interfaceId: string, componentId?: string, ...componentConfig): Promise<T>;
    /**
     * @description
     * Locate the implementation for a provided interfaceId and/or componentId
     * @method
     * @param {string} interfaceId -- interfaceId associated with the componente
     * @param {string} componentId -- id of the component which needs to be located
     * @param {...componentConfig} componentConfig -- list of configs to initialize the component. Only considered when componentId is passed
     * @returns {Promise} - a promise object that resolves to a component class or object
     * @public
     */
    locateImplementation<T>(interfaceId: string, componentId?: string, ...componentConfig): Promise<T> {
      if (!interfaceId) {
        return Promise.reject(new Error('invalid arguments - supply a valid interface id'));
      }
      if (!this._initialized) {
        this.initialize();
      }
      if (!this._implementations[interfaceId]) {
        return Promise.reject(new Error('No such interface known'));
      }
      if (componentId) {
        if (discovery.dropins[componentId]?.implements === interfaceId || discovery.providers[componentId]?.implements === interfaceId) {
          return this.loadComponent<T>(interfaceId, componentId, ...componentConfig);
        }
        if (this._implementations[interfaceId].implementations[componentId]?.implementation) {
          return this.loadImplementation<T>(
            interfaceId,
            componentId,
            this._implementations[interfaceId].implementations[componentId]?.implementation,
            ...(this._implementations[interfaceId].implementations[componentId]?.implementationConfig || componentConfig),
          );
        }
        return Promise.reject(new Error('Supplied component is not registered with supplied interface id'));
      }
      const interfaceImplementations = this._implementations[interfaceId];
      if (interfaceImplementations.activeImplementation) {
        if (typeof interfaceImplementations.activeImplementation === 'string') {
          return this.loadComponent<T>(
            interfaceId,
            interfaceImplementations.activeImplementation,
            ...interfaceImplementations.activeImplementationConfig,
          );
        }
        return this.loadImplementation<T>(
          interfaceId,
          componentId,
          interfaceImplementations.activeImplementation,
          ...interfaceImplementations.activeImplementationConfig,
        );
      }
      if (interfaceImplementations.defaultImplementation) {
        if (typeof interfaceImplementations.defaultImplementation === 'string') {
          return this.loadComponent<T>(
            interfaceId,
            interfaceImplementations.defaultImplementation,
            ...(interfaceImplementations.defaultImplementationConfig || componentConfig),
          );
        }
        return this.loadImplementation<T>(
          interfaceId,
          componentId,
          interfaceImplementations.defaultImplementation,
          ...(interfaceImplementations.defaultImplementationConfig || componentConfig),
        );
      }
      if (interfaceImplementations.implementations && interfaceImplementations.implementations.length === 1) {
        return this.loadComponent(interfaceId, interfaceImplementations.implementations[0]);
      }
      return Promise.reject(new Error('Zero or Multiple implementations for a given interface with no active or default one'));
    }

    loadComponent(interfaceId: string, componentId: string, ...config):Promise<any>;
    loadComponent<T>(interfaceId: string, componentId: string, ...config):Promise<T>
    /**
     * @description
     * Load the platform component for an interfaceId with provided config
     * @method
     * @param {string} interfaceId -- interfaceId associated with the component
     * @param {string} componentId -- the id of the component used to register it in package.json
     * @param {...config} config -- list of configs to initialize the component
     * @returns {Promise} - a promise object that resolves to a component class or object
     * @private
     */
    loadComponent<T>(interfaceId: string, componentId: string, ...config):Promise<T> {
      // first make sure the bootstrap portion is loaded
      return discovery._loadBootstrap(componentId, discovery.dropins[componentId] ? 'dropins' : 'providers').then(
        () => new Promise((resolve, reject) => {
          let loadPromise;

          try {
            const component = discovery.dropins[componentId] || discovery.providers[componentId];
            if (!component.bootstrap || typeof component.bootstrap.load !== 'function') {
              reject(new TypeError(`Bootstrap for '${componentId}' has no load method`));
              return;
            }
            loadPromise = component.bootstrap.load(component.public_path);
            // Ensure component.bootstrap.dependencies are all registered
            loadPromise.then(Cls => resolve(this.loadImplementation<T>(interfaceId, componentId, Cls, ...config))).catch(reject);
          } catch (e) {
            // This should only occur if the "load" method fails before it can
            // return a Promise.
            reject(e);
          }
        }));
    }

    loadImplementation(interfaceId: string, Cls, ...config): Promise<any>;
    loadImplementation<T>(interfaceId: string, Cls, ...config): Promise<T>;
    /**
     * @description
     * Initialize the component implementation with provided config
     * @method
     * @param {string} interfaceId -- interfaceId associated with the component class
     * @param {string} componentId -- id associated with corresponding implementation if any
     * @param {object} Cls -- Cls of the component which needs to be initialized
     * @param {...config} config -- list of configs to initialize the component
     * @returns {Promise} - a promise object that resolves to a component class or object
     * @private
     */
    loadImplementation<T>(interfaceId, componentId: string, Cls, ...config): Promise<T> {
      return new Promise(resolve => {
        let instance = componentId
          ? this._implementations[interfaceId].implementations[componentId]?.instance
          : this._implementations[interfaceId].instance;
        if (!instance) {
          // Determine better ways to detect if it is a React component or function component
          if (interfaceId.startsWith('React.')) {
            instance = Cls;
          } else if (typeof Cls.getInstance === 'function') {
            instance = Cls.getInstance(...config);
          } else if (typeof Cls === 'function') {
            if (Cls.name !== 'getInstance' && isClass(Cls)) {
              instance = new Cls(...config).ready();
            } else {
              instance = Cls(...config);
            }
          } else {
            instance = Cls;
          }
          if (componentId) {
            (this._implementations[interfaceId].implementations[componentId] ??= {}).instance = instance;
          } else {
            this._implementations[interfaceId].instance = instance;
          }
        }
        resolve(instance as T);
      });
    }

    useLocateImplementation<T>(interfaceId: string, componentId?: string, ...componentConfig): T | null {
      const [instance, setInstance] = React.useState<T | null>(null);
      const [, setError] = React.useState<Error | null>(null);

      React.useEffect(() => {
        const load = async () => {
          try {
            const impl = await this.locateImplementation<T>(interfaceId, componentId, componentConfig);
            // impl can be a function - so we have to set it from withing function call
            // otherwise set state would try to invoke it
            setInstance(() => impl);
          } catch (e) {
            setError(() => {
              throw e;
            });
          }
        };

        load();
      }, [interfaceId, componentId, ...componentConfig]);

      return instance;
    }

    useRegisterImplementation<T>(interfaceId: string, component: T | ((...config: any[]) => T) | { new (...config: any[]): T }, ...config: any[]): void {
      React.useEffect(() => {
        this.registerImplementationWith<T>(interfaceId, component, ...config);
      }, [interfaceId, component, ...config]);
    }

    useRegisterImplementationAndId<T>(interfaceId: string, componentId: string, component: T | ((...config: any[]) => T) | { new (...config: any[]): T }, ...config: any[]): void {
      React.useEffect(() => {
        this.registerImplementationAndIdWith<T>(interfaceId, componentId, component, ...config);
      }, [interfaceId, componentId, component, ...config]);
    }

    useRegisterDefaultImplementation<T>(interfaceId: string, component: T | ((...config: any[]) => T) | { new (...config: any[]): T }, ...config: any[]): void {
      React.useEffect(() => {
        this.registerDefaultImplementationWith<T>(interfaceId, component, ...config);
      }, [interfaceId, component, ...config]);
    }
}

export const webPlatform = new WebPlatform();
